import type { FC } from 'react';
import { memo } from 'react';
import SectionHeader from './SectionHeader';

import Image from '../../../shared/components/Image';
import { getSectionIconBaseName } from '../../../shared/utils/sectionIcon';

import { useGetTranslatedSectionName } from '../hooks';
import { useTranslation } from 'react-i18next';
import { useViewMode } from '../../../store/app/appStore';
import { getSectionName } from '../../../utils/getSectionName';
import { testIds } from '../../../core/testIds';

import { HOT_AND_COLD_SECTION_NAME } from "@repo/common";
import { SINGLE_VIEW_MODE } from "../../../constants";

import styles from './SectionHeader.module.css';

interface HeaderProps {
  name: string;
  gamesCount?: number;
  isActive: boolean;
  handleViewToggle?: () => void;
}

const DefaultSectionHeader: FC<HeaderProps> = ({
  name,
  gamesCount,
  isActive: _isActive = false,
  handleViewToggle = () => {},
}) => {
  const viewMode = useViewMode();
  const sectionName = useGetTranslatedSectionName(name);
  const resolvedName = getSectionName(name);
  const titleTestId = testIds.sections.title(resolvedName);
  const { t } = useTranslation();

  const isInSingleView = viewMode === SINGLE_VIEW_MODE
  const isHotColdSection = name === HOT_AND_COLD_SECTION_NAME;
  const baseName = getSectionIconBaseName(name);
  const showAllGames = gamesCount && gamesCount > 0;

  return (
    <SectionHeader>
      <SectionHeader.Left>
        <>
          {!isInSingleView && (
            <span className={styles.iconWrapper}>
              <Image
                src={`/icons/${baseName}_icon`}
                manualAffix=".svg"
                alt={`${sectionName} icon`}
                className={styles.iconImage}
              />
            </span>
          )}
          {!isInSingleView && (
            <span className={styles.sectionName} data-testid={titleTestId}>
              {sectionName}
            </span>
          )}
        </>
      </SectionHeader.Left>
      <SectionHeader.Center>
        {isInSingleView && (
          <span className={styles.sectionName} data-testid={titleTestId}>
            {sectionName} ({gamesCount})
          </span>
        )}
      </SectionHeader.Center>
      <SectionHeader.Right>
        {!isInSingleView && showAllGames && !isHotColdSection ? (
          <span
            className={styles.allGamesLink}
            onClick={handleViewToggle}
            data-testid={testIds.sections.viewAllButton(resolvedName)}
          >
            {t('generic_view_all', 'All')} ({gamesCount})
            <svg
              className={styles.arrowIcon}
              xmlns="http://www.w3.org/2000/svg"
              width="7"
              height="12"
              viewBox="0 0 7 12"
              fill="none"
            >
              <path
                id="chevron"
                fill="#fff"
                d="M6.80936 5.53199L1.50935 0.191994C1.44895 0.131221 1.37712 0.0829925 1.298 0.050082C1.21889 0.0171715 1.13404 0.000228882 1.04836 0.000228882C0.962667 0.000228882 0.877822 0.0171715 0.798706 0.050082C0.71959 0.0829925 0.647765 0.131221 0.587357 0.191994L0.197357 0.584994C0.0754609 0.708669 0.00712585 0.875345 0.00712585 1.04899C0.00712585 1.22264 0.0754609 1.38932 0.197357 1.51299L4.64735 5.99999L0.190353 10.489C0.0684571 10.6127 0.00012207 10.7793 0.00012207 10.953C0.00012207 11.1266 0.0684571 11.2933 0.190353 11.417L0.580353 11.81C0.640761 11.8708 0.712594 11.919 0.79171 11.9519C0.870826 11.9848 0.955663 12.0018 1.04135 12.0018C1.12704 12.0018 1.21188 11.9848 1.291 11.9519C1.37012 11.919 1.44195 11.8708 1.50236 11.81L6.80936 6.46499C6.93127 6.34052 6.99954 6.17322 6.99954 5.99899C6.99954 5.82476 6.93127 5.65647 6.80936 5.53199Z"
              />
            </svg>
          </span>
        ) : null}
      </SectionHeader.Right>
    </SectionHeader>
  );
};

export default memo(DefaultSectionHeader);
